import { PhotoSwipeLightbox } from 'vendor';

const KIT_ZOOM_BG = '#f5f2ec';
const MAX_ZOOM = 3;

let kitZoomLightbox = null;

function getKitZoomLightbox() {
  if (kitZoomLightbox) return kitZoomLightbox;

  kitZoomLightbox = new PhotoSwipeLightbox({
    pswpModule: () => import('photoswipe'),
    bgOpacity: 1,
    maxZoomLevel: MAX_ZOOM,
    closeTitle: window.themeVariables?.strings?.closeGallery || 'Close',
    zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    close: false,
    zoom: false
  });

  kitZoomLightbox.on('uiRegister', () => {
    kitZoomLightbox.pswp.ui.registerElement({
      name: 'kit-close-button',
      className: 'circle-button circle-button--xl hover:animate-icon-block',
      ariaLabel: window.themeVariables?.strings?.closeGallery || 'Close',
      order: 2,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
      onClick: () => kitZoomLightbox.pswp.close()
    });
  });

  kitZoomLightbox.on('afterInit', () => {
    kitZoomLightbox.pswp.element?.classList.add('kit-builder-pswp');
    const bg = kitZoomLightbox.pswp.element?.querySelector('.pswp__bg');
    if (bg) bg.style.setProperty('background', KIT_ZOOM_BG, 'important');
  });

  kitZoomLightbox.init();
  return kitZoomLightbox;
}

function probeImageSize(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({
      width: img.naturalWidth || 2000,
      height: img.naturalHeight || 2500
    });
    img.onerror = () => resolve({ width: 2000, height: 2500 });
    img.src = src;
  });
}

async function openKitProductZoom(sourceImg) {
  const displaySrc = sourceImg.currentSrc || sourceImg.getAttribute('src');
  if (!displaySrc) return;

  // Prefer higher-res zoom URL when present; fall back to display src
  const zoomSrc = sourceImg.getAttribute('data-zoom-src') || displaySrc;

  // Use display dimensions for instant open; scale up when a hi-res zoom src is used
  let width = sourceImg.naturalWidth;
  let height = sourceImg.naturalHeight;
  if (!width || !height) {
    const dims = await probeImageSize(displaySrc);
    width = dims.width;
    height = dims.height;
  }

  if (zoomSrc !== displaySrc && width > 0) {
    const targetWidth = 2000;
    const ratio = height / width;
    width = targetWidth;
    height = Math.round(targetWidth * ratio);
  }

  const lightbox = getKitZoomLightbox();
  lightbox.loadAndOpen(0, [{
    src: zoomSrc,
    // Already-loaded display image shows immediately while hi-res streams in
    msrc: displaySrc,
    width,
    height,
    alt: sourceImg.getAttribute('alt') || ''
  }]);
}

function isZoomableStepImage(el) {
  if (!el?.classList?.contains('kit-builder__step-product-image')) return false;
  return !!el.closest('#step-1-content, #step-2-content, #step-3-content');
}

function setupKitStepImageZoom() {
  const kitBuilder = document.querySelector('.kit-builder-clean');
  if (!kitBuilder) return;

  kitBuilder.querySelectorAll(
    '#step-1-content .kit-builder__step-product-image, #step-2-content .kit-builder__step-product-image, #step-3-content .kit-builder__step-product-image'
  ).forEach((img) => {
    if (img.getAttribute('tabindex') == null) img.setAttribute('tabindex', '0');
    img.setAttribute('role', 'button');
    const baseAlt = img.getAttribute('alt') || 'Product image';
    img.setAttribute('aria-label', baseAlt + ' — click or press Enter to enlarge');
  });

  kitBuilder.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('.kit-builder__side-arrow')) return;
    const img = t.closest('.kit-builder__step-product-image');
    if (!isZoomableStepImage(img)) return;
    e.preventDefault();
    openKitProductZoom(img);
  });

  kitBuilder.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = e.target;
    if (!(t instanceof Element) || !t.classList.contains('kit-builder__step-product-image')) return;
    if (!isZoomableStepImage(t)) return;
    e.preventDefault();
    openKitProductZoom(t);
  });
}

function setupKitModalImageZoom() {
  document.addEventListener('click', (event) => {
    if (!window.matchMedia('(max-width: 699px)').matches) return;
    if (Date.now() < (window.kitProductModalDidSwipeUntil || 0)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const image = target.closest('.kit-builder__modal-slide img[data-zoom-src]');
    if (!image) return;
    event.preventDefault();
    openKitProductZoom(image);
  });

  document.addEventListener('keydown', (event) => {
    if (!window.matchMedia('(max-width: 699px)').matches) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const image = target.closest('.kit-builder__modal-slide img[data-zoom-src]');
    if (!image) return;
    event.preventDefault();
    openKitProductZoom(image);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupKitStepImageZoom();
    setupKitModalImageZoom();
  });
} else {
  setupKitStepImageZoom();
  setupKitModalImageZoom();
}
